import { FC } from 'react';
import { Check } from 'lucide-react';
import { cn } from '../../utils/cn';
import { BookingStep } from '../../types/booking.types';

interface Step {
  number: BookingStep;
  title: string;
  shortTitle: string;
}

const STEPS: Step[] = [
  { number: 1, title: 'Select Service', shortTitle: 'Service' },
  { number: 2, title: 'Choose Therapist', shortTitle: 'Therapist' },
  { number: 3, title: 'Pick Date & Time', shortTitle: 'Schedule' },
  { number: 4, title: 'Your Details', shortTitle: 'Details' },
  { number: 5, title: 'Review & Confirm', shortTitle: 'Confirm' },
];

interface ProgressIndicatorProps {
  currentStep: BookingStep;
  onStepClick?: (step: BookingStep) => void;
  completedSteps?: BookingStep[];
}

export const ProgressIndicator: FC<ProgressIndicatorProps> = ({
  currentStep,
  onStepClick,
  completedSteps = [],
}) => {
  const isStepCompleted = (step: BookingStep): boolean => {
    return completedSteps.includes(step) || step < currentStep;
  };

  const isStepClickable = (step: BookingStep): boolean => {
    // Can click on completed steps or the next step after current
    return isStepCompleted(step) || step <= currentStep;
  };

  return (
    <nav aria-label="Booking progress" className="mb-8">
      <div className="backdrop-blur-sm bg-white/70 rounded-2xl border border-primary-100/50 p-4 md:p-6 shadow-sm">
        {/* Desktop Progress */}
        <ol className="hidden md:flex items-center justify-between">
          {STEPS.map((step, index) => {
            const isCompleted = isStepCompleted(step.number);
            const isCurrent = step.number === currentStep;
            const isClickable = isStepClickable(step.number);

            return (
              <li key={step.number} className="flex-1 relative">
                {/* Connector Line */}
                {index < STEPS.length - 1 && (
                  <div
                    className={cn(
                      'absolute top-5 left-1/2 w-full h-0.5',
                      isCompleted ? 'bg-primary-600' : 'bg-gray-200'
                    )}
                    aria-hidden="true"
                  />
                )}

                {/* Step Circle and Label */}
                <button
                  type="button"
                  onClick={() => isClickable && onStepClick?.(step.number)}
                  disabled={!isClickable || !onStepClick}
                  className={cn(
                    'relative flex flex-col items-center group',
                    isClickable && onStepClick && 'cursor-pointer',
                    !isClickable && 'cursor-not-allowed'
                  )}
                  aria-current={isCurrent ? 'step' : undefined}
                  aria-label={`Step ${step.number}: ${step.title}${isCompleted ? ' (completed)' : isCurrent ? ' (current)' : ''}`}
                >
                  {/* Circle */}
                  <span
                    className={cn(
                      'relative z-10 flex h-10 w-10 items-center justify-center rounded-full border-2 transition-all duration-300',
                      isCompleted && 'border-primary-600 bg-primary-600 text-white',
                      isCurrent &&
                        !isCompleted &&
                        'border-primary-600 bg-white text-primary-600 ring-4 ring-primary-200/50',
                      !isCompleted && !isCurrent && 'border-gray-300 bg-white text-gray-400',
                      isClickable && onStepClick && !isCurrent && 'group-hover:border-primary-400'
                    )}
                  >
                    {isCompleted ? (
                      <Check className="h-5 w-5" aria-hidden="true" />
                    ) : (
                      <span className="text-sm font-semibold">{step.number}</span>
                    )}
                  </span>

                  {/* Label */}
                  <span
                    className={cn(
                      'mt-2 text-sm font-medium text-center',
                      isCurrent && 'text-primary-600',
                      isCompleted && !isCurrent && 'text-gray-700',
                      !isCompleted && !isCurrent && 'text-gray-400'
                    )}
                  >
                    {step.title}
                  </span>
                </button>
              </li>
            );
          })}
        </ol>

        {/* Mobile Progress */}
        <div className="md:hidden">
          {/* Progress Bar */}
          <div className="mb-4">
            <div className="flex justify-between text-sm mb-2">
              <span className="font-medium text-gray-700">
                Step {currentStep} of {STEPS.length}
              </span>
              <span className="text-gray-500">
                {STEPS.find((s) => s.number === currentStep)?.title}
              </span>
            </div>
            <div className="h-2 bg-primary-100/50 rounded-full overflow-hidden">
              <div
                className="h-full bg-primary-600 transition-all duration-300"
                style={{ width: `${(currentStep / STEPS.length) * 100}%` }}
              />
            </div>
          </div>

          {/* Step Pills */}
          <div className="flex gap-2 overflow-x-auto pb-2">
            {STEPS.map((step) => {
              const isCompleted = isStepCompleted(step.number);
              const isCurrent = step.number === currentStep;
              const isClickable = isStepClickable(step.number);

              return (
                <button
                  key={step.number}
                  type="button"
                  onClick={() => isClickable && onStepClick?.(step.number)}
                  disabled={!isClickable || !onStepClick}
                  className={cn(
                    'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors',
                    isCompleted && 'bg-primary-100/80 text-primary-700',
                    isCurrent && !isCompleted && 'bg-primary-600 text-white',
                    !isCompleted &&
                      !isCurrent &&
                      'bg-white/50 text-gray-400 border border-gray-200/50',
                    isClickable && onStepClick && 'hover:opacity-80'
                  )}
                  aria-current={isCurrent ? 'step' : undefined}
                >
                  {isCompleted ? (
                    <Check className="h-3.5 w-3.5" aria-hidden="true" />
                  ) : (
                    <span className="font-medium">{step.number}</span>
                  )}
                  <span>{step.shortTitle}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </nav>
  );
};
